import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { AppStoreIconBadge, GooglePlayIconBadge } from "@/components/AppBadges";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLocale } from "@/i18n/detect";
import { useExtra } from "@/i18n/extra";

import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";

type NavLink = { label: string; to?: string; href?: string };

const Navbar = () => {
  const { pathname } = useLocation();
  const { ui } = useLocale();
  const extra = useExtra();

  const productLinks: NavLink[] = [
    { label: ui.nav.features, to: "/features" },
    { label: ui.nav.pricing, to: "/pricing" },
    { label: ui.nav.integrations, to: "/integrations" },
    { label: ui.nav.industries, to: "/industries" },
    { label: extra.nav.videos, to: "/videos" },
    { label: ui.nav.apiDocs, href: "https://docs.freecrm.com" },
    { label: extra.nav.support, href: "https://help.freecrm.com/" },
  ];

  const companyLinks: NavLink[] = [
    { label: ui.nav.about, to: "/about" },
    { label: ui.nav.customers, to: "/customers" },
    { label: ui.nav.contact, href: "https://help.freecrm.com/hc/en-us/requests/new" },
  ];

  const linkClass = (to: string) =>
    `text-sm font-medium transition-colors hover:text-secondary ${
      pathname === to ? "text-secondary" : "text-muted-foreground"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/80 backdrop-blur-md">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display text-xl font-bold text-secondary">
          <img src="/favicon.png" alt="FreeCRM logo" className="h-8 w-8 object-contain" />
          FreeCRM
        </Link>
        <nav className="hidden items-center gap-7 md:flex">
          {productLinks.map((l) =>
            l.href ? (
              <a
                key={l.href}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-secondary"
              >
                {l.label}
              </a>
            ) : (
              <Link key={l.to} to={l.to} className={linkClass(l.to)}>
                {l.label}
              </Link>
            )
          )}
          <Link to="/about" className={linkClass("/about")}>
            {ui.nav.about}
          </Link>
        </nav>
        <div className="flex items-center gap-2">
          <div className="hidden items-center gap-1.5 lg:flex">
            <AppStoreIconBadge />
            <GooglePlayIconBadge />
          </div>
          <LanguageSwitcher />
          <Button variant="ghost" className="hidden sm:inline-flex" asChild>

            <a href="https://ui.freecrm.com/" target="_blank" rel="noopener noreferrer">
              {ui.nav.login}
            </a>
          </Button>
          <Button variant="hero" asChild>
            <a href="https://ui.freecrm.com/register" target="_blank" rel="noopener noreferrer">
              {ui.nav.getFreeCRM}
            </a>
          </Button>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label={ui.nav.openMenu}>
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <nav className="mt-8 flex flex-col gap-1">
                {[...productLinks, ...companyLinks].map((l) =>
                  l.href ? (
                    <a
                      key={l.href}
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-md px-3 py-2 text-base font-medium text-secondary hover:bg-muted"
                    >
                      {l.label}
                    </a>
                  ) : (
                    <Link
                      key={l.to}
                      to={l.to}
                      className="rounded-md px-3 py-2 text-base font-medium text-secondary hover:bg-muted"
                    >
                      {l.label}
                    </Link>
                  )
                )}
              </nav>
              <div className="mt-6 border-t border-border pt-4">
                <LanguageSwitcher variant="full" />
              </div>

            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
